import axios from "axios";
import {
    cleanQuestions,
    defaultActiveQuestions,
    firstEmptyLabel,
    firstMissingOptions,
    LOCATION_QUESTION_KEY,
} from "../../screening/QuestionsBuilder";

// Side-effect + pure helpers for the sequence builder wizard, kept out of the view layer so
// the create orchestration and validation are testable in isolation. The create path mirrors
// the classic SequenceCreatorModal's handleCreateSave so both produce identical backend rows;
// it is duplicated (not shared) on purpose — the classic modal stays untouched, and is still
// the editor for outreaches with a combined Email + WhatsApp step (see isCombinedServerStep).

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

export const FORM_LINK_TOKEN = "{{form_link}}";

// Variable tokens the backend resolves per-candidate at send time. Labels are display-only —
// the tokens must match the backend's placeholder contract, so this list mirrors the classic modal.
export const VARIABLES = [
    { label: "Candidate Name", token: "{{candidate_name}}" },
    // These three are about where the candidate is *now* — their current title, employer
    // and location. "Current Role" rather than "Role" because the vacancy below is the
    // one recruiters reach for, and the two were easy to confuse.
    { label: "Current Role", token: "{{candidate_role}}" },
    { label: "Current Company", token: "{{candidate_company}}" },
    { label: "Location", token: "{{candidate_location}}" },
    // The job being hired for. Lets one outreach be reused across roles instead of naming
    // one in the copy; resolved per enrollment — see resolveJobRole in enrollmentApi.
    { label: "Job Role", token: "{{job_role}}" },
];
export const FORM_LINK_VARIABLE = { label: "Screening Form Link", token: FORM_LINK_TOKEN };

// _uid is a stable client id used only as a React/drag key — it is never sent to the backend.
export const blankStep = (delayDays = 0) => ({
    _uid: `s_${Math.random().toString(36).slice(2, 10)}`,
    channel: "email",
    subject: "",
    body: "",
    delayDays,
    sendAt: "09:00",
});

export const isCallStep = (s) => s?.channel === "call";
export const isWhatsAppStep = (s) => s?.channel === "whatsapp";
// Neither a call nor a WhatsApp step carries written copy of its own.
export const isEmailStep = (s) => !isCallStep(s) && !isWhatsAppStep(s);

// A saved step (server shape) that sends an email and a WhatsApp message together. The classic
// modal used to offer this as "Email + WhatsApp"; the wizard has one channel per step, so it
// would load such a step as WhatsApp alone and hide its email. Those outreaches stay on the
// classic modal until the wizard can show both halves.
export const isCombinedServerStep = (s) => !!s?.template_id && !!s?.whatsapp_template_id;

// Meta templates are positional ({{1}}, {{2}}…); show the mapped candidate field instead of the
// raw index so the preview reads like the message the candidate receives.
export const renderWaPreview = (tmpl) => {
    if (!tmpl) return "";
    const labels = Object.fromEntries(VARIABLES.map((v) => [v.token, v.label]));
    return (tmpl.body || "").replace(/\{\{(\d+)\}\}/g, (m, n) => {
        const param = (tmpl.params || [])[Number(n) - 1];
        if (!param) return m;
        return labels[param] || param;
    });
};

export const fetchWhatsAppTemplates = async () => {
    const { data } = await axios.get(`${SOURCING_URL}/whatsapp/templates`);
    return data || [];
};

export const stripHtml = (html) =>
    (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

// Only an email step is authored here: a call step's script is the screening form, and a WhatsApp
// step's copy lives in the pre-approved Meta template (it just needs one picked).
const stepHasContent = (s) =>
    isCallStep(s)
        ? true
        : isWhatsAppStep(s)
        ? !!s.whatsappTemplateId
        : !!(s.subject?.trim() || stripHtml(s.body));

// Only email steps can carry the form link — call and WhatsApp steps have no copy to put it in.
export const hasFormLink = (steps) =>
    steps.some(
        (s) =>
            isEmailStep(s) &&
            ((s.body || "").includes(FORM_LINK_TOKEN) || (s.subject || "").includes(FORM_LINK_TOKEN))
    );

// delay_days is cumulative days-from-start and must be strictly increasing; step 0 is always 0.
// Re-run after any reorder / insert / delete so the ladder stays valid.
export const minDelayFor = (steps, idx) => steps[idx - 1].delayDays + 1;

export const normalizeDelays = (steps) => {
    const out = steps.map((s) => ({ ...s }));
    for (let i = 1; i < out.length; i++) {
        const min = minDelayFor(out, i);
        if (!Number.isFinite(out[i].delayDays) || out[i].delayDays < min) out[i].delayDays = min;
    }
    return out;
};

// Options for the wait-time dropdown on a non-first step: every day from the previous step's
// day + 1 up to a generous ceiling, plus the step's current value (in case it falls outside that
// window, e.g. right after a reorder) so the <select> never mismatches its bound value.
const MAX_WAIT_DAYS_AHEAD = 30;
export const validWaitDayOptions = (steps, idx) => {
    const min = minDelayFor(steps, idx);
    const opts = new Set();
    for (let d = min; d <= min + MAX_WAIT_DAYS_AHEAD; d++) opts.add(d);
    opts.add(steps[idx].delayDays);
    return [...opts].sort((a, b) => a - b);
};

// Display title for a step's rail/review card.
export const stepTitle = (step) =>
    isCallStep(step) ? "AI Call" : isWhatsAppStep(step) ? "WhatsApp" : "Email Outreach";

// Truncated preview line for a step's rail/review card — subject if present, else stripped body.
export const stepPreviewText = (step, max = 60) => {
    // Neither of these has authored copy to preview.
    if (isCallStep(step)) return "Asks your screening questions";
    if (isWhatsAppStep(step)) return step.whatsappTemplateId ? "Approved template" : "No template picked";
    const text = step.subject?.trim() || stripHtml(step.body);
    return text.length > max ? `${text.slice(0, max).trim()}…` : text;
};

/* Why the Questions step can't be left yet, as a sentence — or null when it can.
 *
 * canLeaveQuestions answers the same question as a boolean; this names the check that
 * failed. Nothing on this step is visibly invalid, so without it the Next button just goes
 * grey: adding "Which of these locations are you open to working from?" offers no cities
 * until you type some, which reads as the button breaking on question number ten. */
export const questionsBlocker = (form) => {
    if (!form.name.trim()) return "Give your outreach a name on the first step.";
    if (firstEmptyLabel(form.questions)) return "One of your questions has no text yet.";
    const cleaned = cleanQuestions(form.questions);
    if (cleaned.length === 0) return "Add at least one question.";
    const missing = firstMissingOptions(cleaned);
    if (missing) {
        const what = missing.key === LOCATION_QUESTION_KEY ? "job location" : "answer option";
        return `“${missing.label}” needs at least one ${what} — add one, or remove the question.`;
    }
    return null;
};

// Per-step gate for the Next button (lighter than full save validation).
export const canLeaveQuestions = (form) => !questionsBlocker(form);

// Form-link presence is checked at click time (see SequenceBuilder's handleNext), not here —
// this only gates on content so the Next button isn't disabled for a missing form link.
export const canLeaveSequence = (form) => {
    if (!form.name.trim() || form.steps.length === 0) return false;
    if (!form.steps.every(stepHasContent)) return false;
    return true;
};

// {* … *} is the fill-in blank an AI draft leaves where it had no fact to work from. Left
// unfilled it would be mailed to candidates verbatim, so it blocks create rather than only
// being highlighted in the editor.
const AI_BLANK = /\{\*[\s\S]*?\*\}/;

// Full validation before create — returns an error string, or null when the form is valid.
export const validateSequence = (form) => {
    if (!form.name.trim()) return "Give your outreach a name.";
    if (form.steps.length === 0) return "Add at least one outreach step.";
    const noTemplateIdx = form.steps.findIndex((s) => isWhatsAppStep(s) && !s.whatsappTemplateId);
    if (noTemplateIdx !== -1) return `Step ${noTemplateIdx + 1} needs a WhatsApp template picked.`;
    const emptyIdx = form.steps.findIndex((s) => !stepHasContent(s));
    if (emptyIdx !== -1) return `Step ${emptyIdx + 1} needs a subject or a message body.`;
    const blankIdx = form.steps.findIndex(
        (s) => AI_BLANK.test(s.body || "") || AI_BLANK.test(s.subject || "")
    );
    if (blankIdx !== -1)
        return `Step ${blankIdx + 1} still has a highlighted {* … *} blank to fill in before this can send.`;
    // The agent reads its questions off the screening form, so a call step is meaningless without one.
    const callIdx = form.steps.findIndex(isCallStep);
    if (callIdx !== -1 && !form.includeScreening)
        return `Step ${callIdx + 1} is an AI call — turn on screening questions so the call has something to ask.`;
    if (form.includeScreening) {
        if (firstEmptyLabel(form.questions))
            return "Fill in the text of every screening question (or remove the empty one).";
        const cleaned = cleanQuestions(form.questions);
        if (cleaned.length === 0) return "Add at least one screening question.";
        const missing = firstMissingOptions(cleaned);
        if (missing) return `Add at least one option to "${missing.label}".`;
        // Only required when there's an email to carry it — a call/WhatsApp-only outreach has no
        // authored copy to place a link in.
        if (form.steps.some(isEmailStep) && !hasFormLink(form.steps))
            return `Add ${FORM_LINK_TOKEN} to a step so candidates can reach the screening form.`;
    } else if (hasFormLink(form.steps)) {
        // Only reachable by turning screening off on an outreach that had it. With no form
        // attached the token resolves to an empty string at send time, so the sentence built
        // around it would reach the candidate with a hole in it.
        return `Screening is off — remove ${FORM_LINK_TOKEN} from your steps, it would send as a blank gap.`;
    }
    return null;
};

// Screening payload: currency only when a CTC question is present; locations from the
// open_to_locations question's options (mirrors the classic modal).
const screeningPayload = (form) => {
    const hasCTC = form.questions.some((q) => q.key === "expected_ctc" || q.key === "current_ctc");
    const locations = (
        form.questions.find((q) => q.key === LOCATION_QUESTION_KEY)?.options || []
    ).filter(Boolean);
    return {
        questions: cleanQuestions(form.questions),
        currency: hasCTC ? form.currency : null,
        locations,
    };
};

const createStep = async (seqId, step, stepNumber, delayDays, { orgId, recruiterId, name }) => {
    // A call step has no copy, so it gets no email template — just the flag the backend reads
    // to schedule an outbound AI call instead of a send.
    if (isCallStep(step)) {
        await axios.post(`${SOURCING_URL}/email/sequences/${seqId}/steps`, {
            step_number: stepNumber,
            delay_days: delayDays,
            is_call_step: true,
        });
        return;
    }
    // A WhatsApp step references a pre-approved Meta template; nothing is authored here either.
    if (isWhatsAppStep(step)) {
        await axios.post(`${SOURCING_URL}/email/sequences/${seqId}/steps`, {
            step_number: stepNumber,
            delay_days: delayDays,
            whatsapp_template_id: step.whatsappTemplateId,
        });
        return;
    }
    const { data: tmpl } = await axios.post(`${SOURCING_URL}/email/templates`, {
        org_id: orgId,
        created_by: recruiterId,
        name: `${name} – Email ${stepNumber}`,
        subject: step.subject,
        body: step.body,
    });
    await axios.post(`${SOURCING_URL}/email/sequences/${seqId}/steps`, {
        step_number: stepNumber,
        delay_days: delayDays,
        template_id: tmpl.id,
    });
};

// Create the sequence, its screening form (server-side, when type === "screening"), and its
// steps. Returns the created sequence row. Throws on the first failing request.
export const createSequence = async (form, { orgId, recruiterId }) => {
    const name = form.name.trim();
    const body = { org_id: orgId, created_by: recruiterId, name, type: form.includeScreening ? "screening" : "plain" };
    if (form.includeScreening) {
        const payload = screeningPayload(form);
        body.questions = payload.questions;
        body.currency = payload.currency;
        body.locations = payload.locations;
    } else {
        body.questions = null;
    }

    const { data: seq } = await axios.post(`${SOURCING_URL}/email/sequences`, body);
    for (let i = 0; i < form.steps.length; i++) {
        // Step 1 always sends at the start of the schedule (delay 0); the rest use their ladder value.
        await createStep(seq.id, form.steps[i], i + 1, i === 0 ? 0 : form.steps[i].delayDays, {
            orgId,
            recruiterId,
            name,
        });
    }
    return seq;
};

// Load an existing sequence into the wizard's form shape. There's no "get one sequence"/"get one
// template" endpoint, so this joins the same list endpoints SequenceManager already fetches.
// Email, WhatsApp and call steps all round-trip. Throws for a combined Email + WhatsApp step,
// which the wizard can't show without hiding half of it — the list routes those to the classic
// modal, so this only guards an edit URL opened directly.
export const fetchSequenceForEdit = async (sequenceId, { orgId, recruiterId }) => {
    const [{ data: seqs }, { data: steps }, { data: templates }, { data: lock }] = await Promise.all([
        axios.get(`${SOURCING_URL}/email/sequences`, { params: { org_id: orgId } }),
        axios.get(`${SOURCING_URL}/email/sequences/${sequenceId}/steps`),
        axios.get(`${SOURCING_URL}/email/templates`, { params: { org_id: orgId, created_by: recruiterId } }),
        // Rides along in the same batch, so knowing whether screening is still flippable
        // costs no extra round trip.
        axios.get(`${SOURCING_URL}/email/sequences/${sequenceId}/type-lock`),
    ]);
    const seq = seqs.find((s) => s.id === sequenceId);
    if (!seq) throw new Error("Outreach not found.");
    if (steps.some(isCombinedServerStep)) {
        throw new Error(
            "This outreach has a step that sends an email and a WhatsApp message together, which " +
            "this editor can't show yet. Open it from the Outreach list to edit it."
        );
    }

    // Only a WhatsApp step needs these — for its template's name on the Review page, which
    // otherwise only learns it when a template is picked in this session.
    const waTemplates = steps.some((s) => s.whatsapp_template_id)
        ? await fetchWhatsAppTemplates().catch(() => [])
        : [];

    let questions = defaultActiveQuestions();
    let currency = "INR";
    let formId = null;
    if (seq.type === "screening") {
        const { data } = await axios.get(`${SOURCING_URL}/email/sequences/${sequenceId}/form`);
        if (data.form) {
            formId = data.form.id;
            questions = (data.form.questions || []).map((q) => ({ options: [], ...q }));
            currency = data.form.currency || "INR";
        }
    }

    const form = {
        name: seq.name,
        includeScreening: seq.type === "screening",
        questions,
        currency,
        startTiming: "immediately",
        steps: [...steps]
            .sort((a, b) => a.step_number - b.step_number)
            .map((s) => {
                const tmpl = templates.find((t) => t.id === s.template_id);
                return {
                    _uid: `s_${s.id}`,
                    _stepId: s.id,
                    _templateId: s.template_id,
                    // Server truth for the channel, so a toggle away from call can be detected
                    // on save (the step PUT can't null template_id / is_call_step).
                    _isCallStep: !!s.is_call_step,
                    _whatsappTemplateId: s.whatsapp_template_id || null,
                    channel: s.is_call_step ? "call" : s.whatsapp_template_id ? "whatsapp" : "email",
                    whatsappTemplateId: s.whatsapp_template_id || null,
                    whatsappTemplateName:
                        waTemplates.find((t) => t.id === s.whatsapp_template_id)?.name || null,
                    subject: tmpl?.subject || "",
                    body: tmpl?.body || "",
                    delayDays: s.delay_days,
                    sendAt: "09:00",
                };
            }),
    };
    // originalIncludeScreening is server truth, kept apart from form.includeScreening — the
    // latter is what the user has since toggled to, and updateSequence needs both to tell
    // "flip the type" from "just save the existing form's questions".
    return {
        form,
        formId,
        originalStepIds: steps.map((s) => s.id),
        originalIncludeScreening: form.includeScreening,
        typeLocked: !!lock?.locked,
    };
};

// Save edits to an existing sequence: name, its screening form (if any), and a diff of its steps
// (delete removed, update existing, create new) — mirrors the classic modal's handleEditSave,
// simplified since the wizard only ever produces single-channel steps.
export const updateSequence = async (
    form,
    { orgId, recruiterId, sequenceId, formId, originalStepIds, originalIncludeScreening }
) => {
    const name = form.name.trim();
    const screeningFlipped = originalIncludeScreening !== form.includeScreening;
    const payload = form.includeScreening ? screeningPayload(form) : null;

    // One PUT carries the rename and, when the screening choice changed, the flip itself —
    // the server creates or archives the attached form, and rejects the whole call with a 409
    // if anyone is enrolled. The toggle is already disabled in that case; this is the backstop
    // for a stale page whose lock state was fetched before someone else enrolled candidates.
    const seqBody = { name };
    if (screeningFlipped) {
        seqBody.type = form.includeScreening ? "screening" : "plain";
        if (payload) Object.assign(seqBody, payload);
    }
    const { data: saved } = await axios.put(`${SOURCING_URL}/email/sequences/${sequenceId}`, seqBody);

    // Unflipped screening sequence: the form already exists, so edit it in place. A flip *to*
    // screening built its form from the payload above, and a flip to plain has none to save.
    if (!screeningFlipped && payload && formId) {
        await axios.put(`${SOURCING_URL}/engagement/forms/${formId}`, payload);
    }

    const finalStepIds = new Set(form.steps.map((s) => s._stepId).filter(Boolean));
    for (const oldId of originalStepIds) {
        if (!finalStepIds.has(oldId)) {
            await axios.delete(`${SOURCING_URL}/email/sequences/${sequenceId}/steps/${oldId}`);
        }
    }

    for (let i = 0; i < form.steps.length; i++) {
        const step = form.steps[i];
        const stepNumber = i + 1;
        const delayDays = i === 0 ? 0 : step.delayDays;

        if (!step._stepId) {
            await createStep(sequenceId, step, stepNumber, delayDays, { orgId, recruiterId, name });
            continue;
        }

        // The step PUT can't null a field, so a step that changed channel has to be recreated to
        // drop the stale template_id / whatsapp_template_id / is_call_step reference.
        const wasCall = !!step._isCallStep;
        const wasWhatsApp = !!step._whatsappTemplateId;
        const channelChanged =
            wasCall !== isCallStep(step) || wasWhatsApp !== isWhatsAppStep(step);
        if (channelChanged) {
            await axios.delete(`${SOURCING_URL}/email/sequences/${sequenceId}/steps/${step._stepId}`);
            await createStep(sequenceId, step, stepNumber, delayDays, { orgId, recruiterId, name });
            continue;
        }

        // An unchanged call step has no template or copy to sync — only its position/timing.
        if (isCallStep(step)) {
            await axios.put(`${SOURCING_URL}/email/sequences/${sequenceId}/steps/${step._stepId}`, {
                step_number: stepNumber,
                delay_days: delayDays,
            });
            continue;
        }

        // A WhatsApp step that stayed WhatsApp may still have swapped which template it points at.
        if (isWhatsAppStep(step)) {
            await axios.put(`${SOURCING_URL}/email/sequences/${sequenceId}/steps/${step._stepId}`, {
                step_number: stepNumber,
                delay_days: delayDays,
                whatsapp_template_id: step.whatsappTemplateId,
            });
            continue;
        }

        let templateId = step._templateId;
        if (templateId) {
            await axios.put(`${SOURCING_URL}/email/templates/${templateId}`, {
                subject: step.subject,
                body: step.body,
            });
        } else {
            const { data: tmpl } = await axios.post(`${SOURCING_URL}/email/templates`, {
                org_id: orgId,
                created_by: recruiterId,
                name: `${name} – Email ${stepNumber}`,
                subject: step.subject,
                body: step.body,
            });
            templateId = tmpl.id;
        }

        await axios.put(`${SOURCING_URL}/email/sequences/${sequenceId}/steps/${step._stepId}`, {
            step_number: stepNumber,
            delay_days: delayDays,
            template_id: templateId,
        });
    }

    // Server truth after the save, so a caller that stays mounted doesn't keep treating the
    // pre-flip type as "original" and re-send a flip that has already happened.
    return { formId: saved?.form_id ?? null, includeScreening: saved?.type === "screening" };
};

/* ── Draft formatting ────────────────────────────────────────────────────────
   Quill stacks consecutive <p> blocks flush against each other, so a draft that is
   correct prose still reads as a wall of text in the editor and in the sent mail.
   These normalise the draft on the way in: one blank line between blocks, and the
   sign-off kept as a single block so the name sits directly under "Best," rather
   than a paragraph away from it. */

const EMPTY_P = "<p><br></p>";
const BLOCK_RE = /<(p|ul|ol|h[1-6]|blockquote)\b[^>]*>[\s\S]*?<\/\1>/gi;
const HAS_BLOCK_RE = /<(p|div|ul|ol|h[1-6]|blockquote)\b/i;
const BLANK_P_RE = /^<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>$/i;
const SIGN_OFF_RE = /^(best|best regards|regards|warm regards|kind regards|thanks|thank you|cheers|sincerely)[,.]?$/i;

const escapeHtml = (s) =>
    s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const blockText = (b) =>
    b.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
const blockInner = (b) =>
    b.replace(/^<[a-z0-9]+\b[^>]*>/i, "").replace(/<\/[a-z0-9]+>$/i, "").trim();

// A block that is *only* "Best," has lost its name, and a name sitting in its own block
// gets pushed a blank line away by the spacing above. Both end up as one "Best,<br>Name".
const signOffWithName = (blocks, name) => {
    const i = blocks.findIndex((b) => SIGN_OFF_RE.test(blockText(b)));
    if (i === -1 || !/<\/p>$/i.test(blocks[i])) return blocks;
    const after = blocks.slice(i + 1);
    if (after.length === 0) {
        if (!name) return blocks;
        return [...blocks.slice(0, i), blocks[i].replace(/<\/p>$/i, `<br>${escapeHtml(name)}</p>`)];
    }
    // More than one block after the sign-off isn't a name — leave it alone.
    if (after.length > 1) return blocks;
    const tail = blockInner(after[0]);
    if (!tail) return blocks.slice(0, i + 1);
    return [...blocks.slice(0, i), blocks[i].replace(/<\/p>$/i, `<br>${tail}</p>`)];
};

export const formatDraftBody = (html, senderName) => {
    if (!html || !html.trim()) return html;
    // The drafter can hand back prose with newlines rather than markup.
    if (!HAS_BLOCK_RE.test(html)) {
        html = html
            .split(/\n{2,}/)
            .map((p) => p.trim())
            .filter(Boolean)
            .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
            .join("");
    }
    const compact = html.replace(/>\s+</g, "><").trim();
    const blocks = compact.match(BLOCK_RE) || [];
    // Only rewrite when those blocks account for the whole draft. Anything with markup we
    // haven't modelled passes through untouched rather than risk losing part of it.
    if (!blocks.length || blocks.join("") !== compact) return html;
    // Dropping existing blanks first makes this idempotent — re-running can't stack them.
    return signOffWithName(blocks.filter((b) => !BLANK_P_RE.test(b)), senderName).join(EMPTY_P);
};

/** Mirrors the drafter's own cap on the instruction — the composer and the brief below
 *  both budget against it, so they can't drift apart. */
export const MAX_AI_INSTRUCTION = 2000;

// An outreach is written once and reused across roles, so at drafting time there usually
// is no role to name — which is the very thing the drafter asks for before it will write.
// Tell it, every time, that the role is a token. Worded conditionally so an instruction
// that *does* name a role still wins.
const JOB_ROLE_BRIEF =
    "This outreach is reused across job roles — if no specific role is named above, write " +
    "the literal placeholder {{job_role}} where the role belongs; it is filled in with the " +
    "real role when candidates are enrolled.";

// Draft a step's email from a free-text instruction. Company facts are resolved server-side
// from the caller's own organisation, so nothing about the employer is sent from here.
// Resolves to { usable: false, message } when the instruction was too vague to draft from —
// that's feedback for the recruiter, not an error, so it isn't thrown.
export const generateEmailDraft = async ({
    instruction,
    senderName,
    stepNumber,
    totalSteps,
    includeScreening,
    outreachName,
}) => {
    const briefed = `${instruction}\n\n${JOB_ROLE_BRIEF}`;
    const { data } = await axios.post(`${SOURCING_URL}/email/generate-draft`, {
        // Send the recruiter's own words untouched rather than risk tripping the length
        // cap, in the rare case they've written right up to it.
        instruction: briefed.length <= MAX_AI_INSTRUCTION ? briefed : instruction,
        sender_name: senderName || null,
        step_number: stepNumber,
        total_steps: totalSteps,
        include_screening: !!includeScreening,
        outreach_name: outreachName || null,
    });
    if (!data?.usable) return data;
    return { ...data, body: formatDraftBody(data.body, senderName) };
};

export const connectMailbox = async ({ orgId, recruiterId }) => {
    const { data } = await axios.get(`${SOURCING_URL}/auth/gmail/connect`, {
        params: { created_by: recruiterId, org_id: orgId },
    });
    return data.auth_url;
};

export const fetchGmailAccount = async ({ orgId, recruiterId, signal }) => {
    const { data } = await axios.get(`${SOURCING_URL}/auth/email-account`, {
        params: { created_by: recruiterId, org_id: orgId },
        signal,
    });
    return data.connected ? data.email : null;
};
